/**
 * Midi Effects Chain holds the various midi effects that are
 * run in series. Any trigger goes through each effect, transforming
 * the triggers and passing it to the next effect.
 * The output of all these effects gets routed to the actual track
 * which itself is responsible for generating the actual sound (if any)
 *
 * Envelopes are triggered here as well, both pre-effect and post-effect
 * depending on the setup of the envelopes
 */
import MidiTrigger from './MidiTrigger.js';
import {flip} from 'genetix/Flip.js';
import {setNotesDown} from 'actions/index.js';
import ParameterContext from 'effects/ParameterContext.js';
import ArpEffect from './ArpEffect.js';
import HarmonyEffect from './HarmonyEffect.js';
import Tone from 'tone';
import {getMidiEffect} from './getMidiEffect.js';
import {POST_FX, PRE_FX} from 'modulators/constants.js';
import {setEffectParameterValues} from 'actions/index.js';
import WarpEffect from './WarpEffect.js';
import TriggerableChain from './TriggerableChain.js';

class MidiEffectsChain extends TriggerableChain(class {}) {

    constructor(track) {
        super();
        this.track = track;
        this.effects = [];
        this.inputedTriggers = [];
        this.noteToTriggers = {};
        this.stepNumber = 0;
        this.times = [];

        this.setupMessageReader();
    }


    addEffect(effect) {
        if (!effect) {
            return;
        }
        this.effects = [... this.effects, effect];
        this.linkEffectParameters();
        effect.track = this.track || this._subSequence;
        effect._subSequence = this._subSequence;
    }

    getEffectById(id) {
        return this.effects.find(e => e.id === id);
    }

    getCopy(track, ignoreId) {
        let midi = new MidiEffectsChain(track);
        midi.effects = this.effects.map(fx => fx.getCopy(track, ignoreId));
        midi.linkEffectParameters();
        return midi;
    }

    linkEffectParameters() {
        this.effects.forEach(
            effect => {
                effect.registerContext(new ParameterContext().forTrack(this.track));
                effect.registerMixSession(this.track.mixSession);
                effect.registerTrack(this.track);
            });
        let values = {};
        for (let e of this.effects) {
            for (let p of e.getParameters()) {
                values = {
                    ... values,
                    ... p.setStore(this.track.store)
                };
            }
        }
        this.track.store.dispatch(
            setEffectParameterValues(values));
    }

    deleteEffect(effect) {
        let index = this.effects.map(e => e.id).indexOf(effect.id);
        this.effects.splice(index, 1);
        this.effects = [... this.effects];
        effect.dispose();
    }

    moveEffectTo(effectFrom, effectTo) {
        if (effectFrom == effectTo) {
            return;
        }

        let newEffects = [];
        if (effectFrom > effectTo) {
            newEffects = this.effects.slice(0, effectTo)
                .concat([this.effects[effectFrom]])
                .concat(this.effects.slice(effectTo, effectFrom));
            
            if (this.effects.length > effectFrom + 1) {
                newEffects = newEffects.concat(this.effects.slice(effectFrom + 1));;
            }
        } else {
            newEffects = this.effects.slice(0, effectFrom)
                .concat(this.effects.slice(effectFrom + 1, effectTo))
                .concat([this.effects[effectTo], this.effects[effectFrom]]);

            if (this.effects.length > effectTo + 1) {
                newEffects = newEffects.concat(this.effects.slice(effectTo + 1));;
            }
        }

        this.effects = newEffects;
    }

    needsStableLookAhead() {
        return this.effects.some(effect => effect.needsStableLookAhead());
    }

    // input all the triggers that should happen for this 1/32 step,
    // pre midi effects chain
    input(stepData, time, currentStep) {
        this.inputedTriggers.push(
            new MidiTrigger(stepData, time, currentStep));
    }

    // outputs all the triggers that should be played post chain
    output(time, stepNumber, actualStepNumber) {
        this.times.push(time);
        this.stepNumber = stepNumber;
        let input = this.inputedTriggers;
        input.forEach(
            trigger => 
                this.triggerAttackReleasePreFXEnvelopes(time, trigger));
        this.setNotesDown(input);

        for (let i in this.effects) {
            let effect = this.effects[i];
            if (!effect.isEnabled()) {
                continue;
            }
            for (let j in input) {
                let trigger = input[j];
                effect.input(trigger);
            }
            input = effect.output(time, stepNumber, actualStepNumber);
        }

        this.triggerAttackReleasePostFXEnvelopes(input);
        return input;
    }


    getDuration(duration) {
        return new Tone.Time('16n') * duration;
    }

    get subSequence() {
        return this.track.subSequence;
    }

    get state() {
        return this.track.sequencerEngine.state;
    }

    dispatch(x) {
        this.track.sequencerEngine.dispatch(x);
    }

    // On each new step scheduling in the PlaySequencerEngine, we clear
    // the inputedTriggers
    onNewStep() {
        this.inputedTriggers = [];

        this.effects.forEach(effect => effect.onNewStep());
    }

    // When "starting" playback of a sequence, we want to
    // clear everything
    clear() {
        this.effects.forEach(
            effect =>
                effect.clear());
    }

    fromJson(json) {
        let bulk = {};
        for (let i in json) {
            let effect = getMidiEffect(json[i], this.track || {sequencerEngine: this.sequencerEngine});
            if (!effect) {
                continue;
            }
            this.addEffect(effect);
            bulk = {
                ... bulk,
                ... effect.fromJson(json[i])
            };
        }
        return bulk;
    }

    getEffectNumber(effect) {
        let i = this.effects.indexOf(effect);

        return i;
    }


    getJson() {
        let json = this.effects.map(effect => effect.getJson());
        return json;
    }

    getAllParameters() {
        let parameters = [];
        this.effects.forEach(
            effect =>
                effect.getParameters()
                .forEach(parameter => parameters.push(parameter)));

        return parameters;
    }

    getAllPatternLockedParameters() {
        return this.getAllParameters()
            .filter(p => p.hasPatternLockedValues());
    }
    
    mate(partner, prob=0.5) {
        let numEffects = Math.max(partner.effects.length, this.effects.length);
        let effects = [];
        for (let i=0; i < numEffects; i++) {
            let effectA = this.effects[i];
            let effectB = partner.effects[i];
            if (flip(prob)) {
                if (effectB) {
                    effects.push(effectB.getCopy(this.track));
                }
            } else if (effectA) {
                effects.push(effectA);
            }
        }

        this.effects = effects;
        this.linkEffectParameters();
    }

    addNoteDown(note) {
        let notesDown = this.state.keyboard.notesDown || [];
        this.dispatch(setNotesDown([... notesDown, note]));
    }

    removeNoteDown(note) {
        let notesDown = this.state.keyboard.notesDown || [];
        notesDown = [... notesDown];
        let index = notesDown.indexOf(note);
        notesDown.splice(index, 1);
        this.dispatch(setNotesDown(notesDown));
    }

    setNotesDown(input) {
        if (input.length === 0 || Tone.Master.context.isOffline) {
            return;
        }
        // first check if its selected track AND piano roll showing
        if (this.track.sequencerEngine.getSelectedTrack() === this.track &&
           this.state.sequencer.showPianoRoll) {
            // then we need to set notes for this input time
            let newNotesDown = input.map(x => x.stepData.transpose);
            let notesDown = this.state.keyboard.notesDown || [];
            this.dispatch(setNotesDown([... notesDown, ... newNotesDown]));

            input.forEach(
                x => setTimeout(() => {
                    if (x.stepData.trackData) {
                        this.removeNoteDown(x.stepData.transpose);
                    }
                }, x.stepData.duration * Tone.Time('16n').toSeconds() * 1000.0));
        }
    }

    stop() {
        this.times = [];
        this.effects.forEach(e => e.stop());
    }

    dispose() {
        if (this._disposed) {
            return;
        }
        this.effects.forEach(
            e => e.dispose());
        this._disposed = true;
    }
}

export default MidiEffectsChain;
